import { useState } from 'react';
import {
  Paper,
  Stack,
  Typography,
  Slider,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  Box
} from '@mui/material';

/**
 * ContextNoteForm Component
 * Quick mood/stress/sleep/symptoms capture
 */

const SYMPTOM_OPTIONS = [
  "Headache",
  "Fatigue",
  "Nausea",
  "Dizziness",
  "Anxiety",
  "None"
];

interface ContextNoteFormProps {
  onSubmit: (note: ContextNoteInput) => Promise<void>;
  onCancel?: () => void;
}

interface ContextNoteInput {
  ts: string;
  noteText?: string;
  mood?: number; // 1-5
  stress?: number; // 1-5
  sleepHours?: number;
  symptoms?: string[];
}

export const ContextNoteForm: React.FC<ContextNoteFormProps> = ({
  onSubmit,
  onCancel
}) => {
  const [mood, setMood] = useState<number>(3);
  const [stress, setStress] = useState<number>(3);
  const [sleepHours, setSleepHours] = useState<number>(7);
  const [symptoms, setSymptoms] = useState<string[]>([]);
  const [noteText, setNoteText] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onSubmit({
        ts: new Date().toISOString(),
        mood,
        stress,
        sleepHours,
        symptoms,
        noteText: noteText || undefined
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSymptomsChange = (
    _event: React.MouseEvent<HTMLElement>,
    newSymptoms: string[]
  ) => {
    setSymptoms(newSymptoms);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={4}>
        <Box>
          <Typography gutterBottom>How are you feeling today?</Typography>
          <Slider
            value={mood}
            onChange={(_, val) => setMood(val as number)}
            min={1}
            max={5}
            marks={[
              { value: 1, label: "😞" },
              { value: 3, label: "😐" },
              { value: 5, label: "😊" }
            ]}
            step={1}
            valueLabelDisplay="auto"
          />
        </Box>

        <Box>
          <Typography gutterBottom>Stress level</Typography>
          <Slider
            value={stress}
            onChange={(_, val) => setStress(val as number)}
            min={1}
            max={5}
            marks
            step={1}
            valueLabelDisplay="auto"
          />
        </Box>

        <Box>
          <Typography gutterBottom>Hours of sleep last night</Typography>
          <Slider
            value={sleepHours}
            onChange={(_, val) => setSleepHours(val as number)}
            min={0}
            max={12}
            step={0.5}
            valueLabelDisplay="auto"
          />
        </Box>

        <Box>
          <Typography gutterBottom sx={{ mb: 1 }}>
            Any symptoms? (select all that apply)
          </Typography>
          <ToggleButtonGroup
            value={symptoms}
            onChange={handleSymptomsChange}
            sx={{ flexWrap: 'wrap', gap: 1 }}
          >
            {SYMPTOM_OPTIONS.map((symptom) => (
              <ToggleButton key={symptom} value={symptom} size="small">
                {symptom}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <TextField
          label="Anything else to note? (optional)"
          multiline
          rows={3}
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="e.g., Had a stressful test today"
        />

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          {onCancel && (
            <Button onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
          >
            Save
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};
